import { useCallback, useRef } from 'react';
import type React from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { CarouselItemContent } from '../../template/imageCarouselTypes';
import { useEditorSidebar } from './editorSidebarContext';
import { isVideoFile, useCardVideoUpload } from './useCardVideoUpload';
import { useLibraryVideoDrop } from './useLibraryVideoDrop';

/**
 * State + handlers for `ImageCarouselSlideCard`: video upload/poll, library-video drag-drop
 * (both onto a media zone and onto the rest of the card), and the sidebar's Media tab.
 */
export function useImageCarouselSlideCardState({
  index,
  item,
  fundOpportunityId,
  backendService,
  onToggleVideo,
  onLibraryVideoAttached,
}: {
  index: number;
  item: CarouselItemContent;
  fundOpportunityId: string;
  backendService: BackendService;
  onToggleVideo: (isVideo: boolean) => void;
  onLibraryVideoAttached: (externalId: string) => void;
}) {
  const { draggingVideoId, openSidebar } = useEditorSidebar();
  const attachRef = useRef(onLibraryVideoAttached);
  attachRef.current = onLibraryVideoAttached;
  const videoDrop = useLibraryVideoDrop(onLibraryVideoAttached);
  const upload = useCardVideoUpload({
    fundOpportunityId,
    backendService,
    attachedExternalId: item.libraryVideoExternalId,
    onAttach: onLibraryVideoAttached,
  });

  const handleVideoFile = (file: File) => {
    if (upload.phase === 'uploading' || upload.phase === 'awaiting-metadata') return;
    if (!item.isVideo) onToggleVideo(true);
    upload.beginUpload(file);
  };

  // A dropped video file replaces this slide's media wherever on the card it lands.
  // The media zones handle (and preventDefault) drops on themselves first; this
  // catches the rest of the card, and keeps the browser from opening the file.
  const handleCardDrop = (event: React.DragEvent) => {
    if (videoDrop.handleDrop(event) || event.defaultPrevented) return;
    const file = Array.from(event.dataTransfer.files ?? []).find(isVideoFile);
    if (!file) return;
    event.preventDefault();
    handleVideoFile(file);
  };

  const handleCardDragOver = (event: React.DragEvent) => {
    if (event.dataTransfer.types.includes('Files')) event.preventDefault();
    videoDrop.onDragOver(event);
  };

  const openLibrary = () =>
    openSidebar('media', {
      label: `slide ${index + 1}`,
      onPick: (externalId: string) => attachRef.current(externalId),
    });

  const getVideoPlaybackUrl = useCallback(
    (externalId: string) => backendService.getVideoPlaybackUrl(externalId),
    [backendService],
  );

  return {
    draggingVideoId,
    videoDrop,
    upload,
    handleVideoFile,
    handleCardDrop,
    handleCardDragOver,
    openLibrary,
    getVideoPlaybackUrl,
  };
}
